import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, Bell, Check, Trash2 } from 'lucide-react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';

const SetReminderModal = ({ isOpen, onClose, customer, isBulk = false, bulkIds = [], onSave, onClear }) => {
  const { t } = useLang();

  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('09:00');
  const [autoReminder, setAutoReminder] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activePreset, setActivePreset] = useState(null);

  // Format YYYY-MM-DD helper
  const formatDateToIso = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (isOpen) {
      if (!isBulk && customer) {
        setSelectedDate(customer.reminder_date || '');
        setSelectedTime(customer.reminder_time || '09:00');
        setAutoReminder(customer.auto_reminder === 1 || customer.auto_reminder === true || customer.auto_reminder === '1');
      } else {
        // Default to tomorrow for bulk
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setSelectedDate(formatDateToIso(tomorrow));
        setSelectedTime('09:00');
        setAutoReminder(true);
        setActivePreset('tomorrow');
      }
    }
  }, [isOpen, customer, isBulk]);

  if (!isOpen) return null;

  const presets = [
    { id: 'today', label: t(LangKeys.reminders_chip_today) || 'आज', days: 0 },
    { id: 'tomorrow', label: t(LangKeys.reminders_chip_tomorrow) || 'कल', days: 1 },
    { id: '3days', label: t(LangKeys.reminders_chip_3days) || '3 दिन बाद', days: 3 },
    { id: '1week', label: t(LangKeys.reminders_chip_1week) || '1 सप्ताह बाद', days: 7 },
    { id: '15days', label: t(LangKeys.reminders_chip_15days) || '15 दिन बाद', days: 15 },
    { id: '1month', label: t(LangKeys.reminders_chip_1month) || '1 माह बाद', days: 30 },
  ];

  const handleApplyPreset = (preset) => {
    setActivePreset(preset.id);
    const target = new Date();
    target.setDate(target.getDate() + preset.days);
    setSelectedDate(formatDateToIso(target));
  };

  const handleCustomDateChange = (e) => {
    setSelectedDate(e.target.value);
    setActivePreset(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate) return;

    setIsSubmitting(true);
    try {
      if (isBulk) {
        await onSave({
          customer_ids: bulkIds,
          reminder_date: selectedDate,
          reminder_time: selectedTime || '09:00',
          auto_reminder: autoReminder ? 1 : 0,
        });
      } else {
        await onSave({
          customer_id: customer.customer_id,
          reminder_date: selectedDate,
          reminder_time: selectedTime || '09:00',
          auto_reminder: autoReminder ? 1 : 0,
        });
      }
      onClose();
    } catch (err) {
      console.error('Failed to save reminder:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = async () => {
    if (isBulk) return;
    setIsSubmitting(true);
    try {
      if (onClear) {
        await onClear(customer.customer_id);
      } else {
        await onSave({
          customer_id: customer.customer_id,
          reminder_date: null,
          reminder_time: '09:00',
          auto_reminder: 0,
        });
      }
      onClose();
    } catch (err) {
      console.error('Failed to clear reminder:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.iconCircle}>
              <Calendar size={20} color="#2563EB" />
            </div>
            <div>
              <h3 style={styles.title}>
                {isBulk
                  ? `${t(LangKeys.reminders_set_due_date) || 'तारीख सेट करें'} (${bulkIds.length} ${t(LangKeys.reminders_selected) || 'चुने गए'})`
                  : `${customer?.customer_name || 'Customer'} - ${t(LangKeys.reminders_set_due_date) || 'तारीख सेट करें'}`}
              </h3>
              {!isBulk && customer && (
                <p style={styles.subtitle}>
                  {t(LangKeys.net_balance) || 'कुल बकाया'}: <span style={styles.amount}>₹{parseFloat(customer.pending_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </p>
              )}
            </div>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>
            <X size={20} color="#64748B" />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Quick Presets */}
          <div style={styles.section}>
            <label style={styles.sectionLabel}>
              {t(LangKeys.reminders_set_due_date) || 'तारीख चुनें (Quick Presets)'}
            </label>
            <div style={styles.presetGrid}>
              {presets.map((p) => {
                const isSelected = activePreset === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => handleApplyPreset(p)}
                    style={{
                      ...styles.presetChip,
                      ...(isSelected ? styles.presetChipActive : {}),
                    }}
                  >
                    {isSelected && <Check size={14} color="#FFFFFF" />}
                    <span>{p.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date & Time Row */}
          <div style={styles.row}>
            <div style={styles.col}>
              <label style={styles.label}>
                <Calendar size={14} color="#64748B" style={{ marginRight: '6px' }} />
                {t(LangKeys.date) || 'तारीख'}
              </label>
              <input
                type="date"
                required
                value={selectedDate}
                onChange={handleCustomDateChange}
                style={styles.input}
              />
            </div>

            <div style={styles.col}>
              <label style={styles.label}>
                <Clock size={14} color="#64748B" style={{ marginRight: '6px' }} />
                {t(LangKeys.reminders_time_lbl) || 'समय'}
              </label>
              <input
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                style={styles.input}
              />
            </div>
          </div>

          {/* Auto Reminder Toggle */}
          <div style={styles.toggleCard} onClick={() => setAutoReminder(!autoReminder)}>
            <div style={styles.toggleLeft}>
              <div style={{ ...styles.toggleIcon, backgroundColor: autoReminder ? '#ECFDF5' : '#F1F5F9' }}>
                <Bell size={18} color={autoReminder ? '#10B981' : '#94A3B8'} />
              </div>
              <div>
                <div style={styles.toggleTitle}>{t(LangKeys.reminders_auto_reminder) || 'ऑटो रिमाइंडर (Auto Reminder)'}</div>
                <div style={styles.toggleDesc}>
                  {t(LangKeys.reminders_auto_reminder_desc) || 'तारीख आने पर अपने आप रिमाइंडर भेजें'}
                </div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={autoReminder}
              onChange={(e) => setAutoReminder(e.target.checked)}
              style={styles.checkbox}
            />
          </div>

          {/* Footer Actions */}
          <div style={styles.footer}>
            {!isBulk && customer?.reminder_date && (
              <button
                type="button"
                onClick={handleClear}
                disabled={isSubmitting}
                style={styles.clearBtn}
              >
                <Trash2 size={16} color="#DC2626" />
                <span>{t(LangKeys.reminders_clear_date) || 'हटाएं'}</span>
              </button>
            )}

            <div style={{ display: 'flex', gap: '10px', marginLeft: 'auto' }}>
              <button type="button" onClick={onClose} style={styles.cancelBtn}>
                {t(LangKeys.close) || 'रद्द करें'}
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !selectedDate}
                style={styles.saveBtn}
              >
                {isSubmitting
                  ? (t(LangKeys.reminders_sending) || 'सुरक्षित हो रहा है...')
                  : (t(LangKeys.reminders_save_date) || 'तारीख सुरक्षित करें')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '16px',
  },
  modal: {
    width: '100%',
    maxWidth: '480px',
    backgroundColor: '#FFFFFF',
    borderRadius: '20px',
    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.25)',
    overflow: 'hidden',
    animation: 'modalSlideUp 0.25s ease-out',
  },
  header: {
    padding: '18px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #E2E8F0',
    backgroundColor: '#F8FAFC',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  iconCircle: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    backgroundColor: '#EFF6FF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #DBEAFE',
  },
  title: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#0F172A',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#64748B',
    margin: '2px 0 0 0',
  },
  amount: {
    fontWeight: '700',
    color: '#DC2626',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '6px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  form: {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  sectionLabel: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  presetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '8px',
  },
  presetChip: {
    padding: '8px 10px',
    borderRadius: '10px',
    border: '1px solid #E2E8F0',
    backgroundColor: '#F8FAFC',
    color: '#334155',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    transition: 'all 0.15s ease',
  },
  presetChipActive: {
    backgroundColor: '#2563EB',
    borderColor: '#2563EB',
    color: '#FFFFFF',
    boxShadow: '0 2px 6px rgba(37, 99, 235, 0.3)',
  },
  row: {
    display: 'flex',
    gap: '12px',
  },
  col: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '12.5px',
    fontWeight: '600',
    color: '#334155',
    display: 'flex',
    alignItems: 'center',
  },
  input: {
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1.5px solid #CBD5E1',
    fontSize: '14px',
    color: '#0F172A',
    outline: 'none',
    boxSizing: 'border-box',
    width: '100%',
    backgroundColor: '#FFFFFF',
  },
  toggleCard: {
    padding: '12px 14px',
    borderRadius: '12px',
    backgroundColor: '#F8FAFC',
    border: '1px solid #E2E8F0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  toggleLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  toggleIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#0F172A',
  },
  toggleDesc: {
    fontSize: '11px',
    color: '#64748B',
    marginTop: '2px',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    accentColor: '#10B981',
    cursor: 'pointer',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: '10px',
    borderTop: '1px solid #F1F5F9',
  },
  clearBtn: {
    padding: '9px 14px',
    borderRadius: '10px',
    border: '1px solid #FECACA',
    backgroundColor: '#FEF2F2',
    color: '#DC2626',
    fontSize: '13px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
  },
  cancelBtn: {
    padding: '10px 16px',
    borderRadius: '10px',
    border: '1px solid #CBD5E1',
    backgroundColor: '#FFFFFF',
    color: '#475569',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  saveBtn: {
    padding: '10px 20px',
    borderRadius: '10px',
    border: 'none',
    backgroundColor: '#2563EB',
    color: '#FFFFFF',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
  },
};

export default SetReminderModal;
